import { Injectable } from '@angular/core';

import { UserCharacteristics, Microenvironment, UserActivity, Intensity, UserExercise, HomeCharacteristics, HomeOperatingConditions, OtherConditions, WeatherConditions, InfiltrationParameters } from '../models/user-inputs';
import { STACK_COEFFICIENTS, WIND_COEFFICIENTS, VENTILATION_RATES } from '../models/data';

@Injectable({
  providedIn: 'root'
})
export class ExposureCalculatorService {

  constructor() { }
  
  infiltrationFactor(pollutant: string, microenvironment: Microenvironment,
    homeInfiltrationFactor: number, infiltrationParameters: InfiltrationParameters): number {
    switch (microenvironment) {
      case Microenvironment.outdoors:
        return 1;
        break;
      case Microenvironment.vehicles:
        if (pollutant == 'PM2.5') {
          return infiltrationParameters.infiltrationFactorPM25Vehicles;
        } else if (pollutant == 'Ozone') {
          return infiltrationParameters.infiltrationFactorOzoneVehicles;
        }
        break;
      case Microenvironment.other:
        if (pollutant == 'PM2.5') {
          return infiltrationParameters.infiltrationFactorPM25NotHomes;
        } else if (pollutant == 'Ozone') {
          return infiltrationParameters.infiltrationFactorOzoneNotHomes;
        }
        break;
      case Microenvironment.home:
        return homeInfiltrationFactor;
        break;
    }
    return 1;
  }
  
  personalExposureFactor(pollutant: string, homeInfiltrationFactor: number,
    userActivity: UserActivity, infiltrationParameters: InfiltrationParameters): number {
    const timeOutdoors = userActivity[Microenvironment.outdoors] / 24;
    const timeInVehicles = userActivity[Microenvironment.vehicles] / 24;
    const timeIndoorsNotHome = userActivity[Microenvironment.other] / 24;
    const timeIndoorsAtHome = 1 - (timeOutdoors + timeInVehicles + timeIndoorsNotHome);
    
    const vehicleInfiltrationFactor = this.infiltrationFactor(pollutant, Microenvironment.vehicles, homeInfiltrationFactor, infiltrationParameters);
    const otherBuildingInfiltrationFactor = this.infiltrationFactor(pollutant, Microenvironment.other, homeInfiltrationFactor, infiltrationParameters);
    const exposureFactor =
        timeOutdoors
        + vehicleInfiltrationFactor * timeInVehicles
        + otherBuildingInfiltrationFactor * timeIndoorsNotHome
        + homeInfiltrationFactor * timeIndoorsAtHome
    return exposureFactor;
  }
  
  bodySurfaceArea(userCharacteristics: UserCharacteristics): number {
    const bodyWeight = userCharacteristics.weight;
    const height = userCharacteristics.height;
      
    const bodySurfaceArea = 0.007184 * Math.pow(height, 0.725) * Math.pow(bodyWeight, 0.425); // m^2
    return bodySurfaceArea;
  }
  
  dose(pollutant: string, outdoorLevelInUg: number, userCharacteristics: UserCharacteristics,
    userActivity: UserActivity, userExercise: UserExercise,
    homeInfiltrationFactor: number, infiltrationParameters: InfiltrationParameters): number {
    
    const intensities = [Intensity.vigorous, Intensity.moderate, Intensity.light, Intensity.sedentary];
    
    // look up ventilation rates based on user characteristics
    let userRates = {
      [Intensity.vigorous]: 0,
      [Intensity.moderate]: 0,
      [Intensity.light]: 0,
      [Intensity.sedentary]: 0
    };
    const ventilationRates = VENTILATION_RATES[userCharacteristics.sex];
    for (const intensity of intensities) {
      for (const [minAge, maxAge, rate] of ventilationRates[intensity]) {
        if (userCharacteristics.age >= minAge &&
            userCharacteristics.age <= maxAge) {
          userRates[intensity] = rate;
          break;
        }
      }
    }
    
    const bodySurfaceArea = this.bodySurfaceArea(userCharacteristics);
    
    let dose = 0;
    
    const envs = [Microenvironment.vehicles, Microenvironment.outdoors, Microenvironment.other, Microenvironment.home];
    let minutesAtHome = 24 * 60;
    for (let env of envs) {
      let minutesInEnv = userActivity[env] * 60;
      if (env == Microenvironment.home) {
        minutesInEnv = minutesAtHome;
      } else {
        minutesAtHome -= minutesInEnv;
      }
      const infiltrationFactor = this.infiltrationFactor(pollutant, env, homeInfiltrationFactor, infiltrationParameters);
      const envExposure = outdoorLevelInUg * infiltrationFactor;
      
      let minutesSedentary = minutesInEnv;
      for (let intensity of intensities) {
        let minutesActivity = userExercise[env][intensity];
        if (intensity == Intensity.sedentary) {
          minutesActivity = minutesSedentary;
        } else {
          minutesSedentary -= minutesActivity;
        }
        dose += envExposure * userRates[intensity] * userCharacteristics.weight * minutesActivity * 0.001 / bodySurfaceArea
      }
    }
    
    return dose;
  }
  
  exposedConditions(userActivity: UserActivity, outdoorTemperature: number, outdoorHumidity: number, otherConditions: OtherConditions, homeOperatingConditions: HomeOperatingConditions): [number, number, number] {
    const timeOutdoors = userActivity[Microenvironment.outdoors] / 24;
    const timeInVehicles = userActivity[Microenvironment.vehicles] / 24;
    const timeIndoorsNotHome = userActivity[Microenvironment.other] / 24;
    const timeIndoorsAtHome = 1 - (timeOutdoors + timeInVehicles + timeIndoorsNotHome);
    
    const exposedTemperature = timeOutdoors * outdoorTemperature +
      timeInVehicles * otherConditions.insideVehicleTemperature +
      timeIndoorsNotHome * otherConditions.indoorNotHomeTemperature +
      timeIndoorsAtHome * homeOperatingConditions.indoorTemperature;
    
    const exposedHumidity = timeOutdoors * outdoorHumidity +
      timeInVehicles * otherConditions.insideVehicleHumidity +
      timeIndoorsNotHome * otherConditions.indoorNotHomeHumidity +
      timeIndoorsAtHome * otherConditions.indoorHomeHumidity;
    
    let exposedHeatIndex = exposedTemperature;
    // calculate heat index if temp >= 80 F and humidity >= 40%
    // https://www.wpc.ncep.noaa.gov/html/heatindex_equation.shtml
    const expTempF = exposedTemperature * 1.8 + 32
    if (expTempF >= 80 && exposedHumidity >= 40) {
      const T = expTempF;
      const RH = exposedHumidity;
      const heatIndexF = -42.379 + 2.04901523*T + 10.14333127*RH - 0.22475541*T*RH - 0.00683783*T*T - 0.05481717*RH*RH + 0.00122874*T*T*RH + 0.00085282*T*RH*RH - 0.00000199*T*T*RH*RH;
      exposedHeatIndex = (heatIndexF - 32) / 1.8
    }
    
    return [exposedTemperature, exposedHumidity, exposedHeatIndex];
  }
}
